import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { portalApi, tournamentApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'

/**
 * Portal Dashboard — landing page after login.
 *
 * Shows a setup checklist at the top if the course hasn't finished onboarding
 * (Location, Branding, Tee Sheet, Launch), so the user always sees what's
 * left to do before they can go live. Once complete, the dashboard shows
 * the real metrics and product catalog.
 */
export default function Dashboard() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { t } = useLanguage()
  const [course, setCourse] = useState(null)
  const [product, setProduct] = useState(null)
  const [packageCount, setPackageCount] = useState(0)
  const [productCatalog, setProductCatalog] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      portalApi.get('/auth/me')
        .then(({ data }) => {
          setCourse(data.course)
          setProduct(data.portalProduct)
        })
        .catch(() => {}),
      // Count packages for the setup checklist; failure is non-fatal
      tournamentApi.get('/play-stay/packages')
        .then(({ data }) => setPackageCount(Array.isArray(data) ? data.length : 0))
        .catch(() => setPackageCount(0)),
      // Full InteGolf product catalog + enrollment status for the cross-product section
      portalApi.get('/products')
        .then(({ data }) => setProductCatalog(Array.isArray(data?.products) ? data.products : []))
        .catch(() => setProductCatalog([])),
    ]).finally(() => setLoading(false))
  }, [])

  function handleSignOut() {
    logout()
    navigate('/login')
  }

  // Derive setup status from the course + product.Settings
  const settings = product?.settings || {}
  const hasLocation = !!(course?.Latitude && course?.Longitude)
  const hasProvider = !!settings.provider
  const hasPackages = packageCount > 0
  const hasBranding = !!settings.branding && (!!settings.branding.logoUrl || !!settings.branding.primaryColor || !!settings.branding.style)

  // Order matters: Location -> Tee Sheet -> Packages -> Branding.
  // Packages comes before branding so the merchant has at least one real
  // package created when they reach the branding editor — the live preview
  // there pulls from the merchant's actual packages instead of placeholder
  // text, so they can see exactly what their widget will look like with
  // their pricing and inclusions applied.
  const steps = [
    {
      key: 'location',
      title: t('sp_set_course_location', 'Set your course location'),
      desc: t('sp_set_course_location_desc', 'We need to know where your course is to find nearby hotels.'),
      complete: hasLocation,
      route: '/portal/location',
    },
    {
      key: 'provider',
      title: t('sp_connect_tee_sheet', 'Connect your tee sheet'),
      desc: t('sp_connect_tee_sheet_desc', 'Choose InteGolf, Tee-On, ChronoGolf, TTC, or Club Prophet for tee time bookings.'),
      complete: hasProvider,
      route: '/portal/tee-sheet',
    },
    {
      key: 'packages',
      title: t('sp_create_first_package', 'Create your first package'),
      desc: t('sp_create_first_package_desc', 'Build at least one Stay & Play package with pricing and inclusions.'),
      complete: hasPackages,
      route: '/portal/packages',
    },
    {
      key: 'branding',
      title: t('sp_brand_widget', 'Brand your booking widget'),
      desc: t('sp_brand_widget_desc', 'Pick a layout and style — preview shows your real package live.'),
      complete: hasBranding,
      route: '/portal/branding',
    },
  ]

  // Setup is complete when all four steps are done. Replaces the legacy
  // product.setupComplete flag (which still exists for backward compat but
  // is no longer the source of truth).
  const isComplete = steps.every(s => s.complete)

  const completedCount = steps.filter(s => s.complete).length
  const progressPct = Math.round((completedCount / steps.length) * 100)

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={headerStyle}>
        <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 32 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {user?.first_name} {user?.last_name}
          </span>
          <button onClick={handleSignOut} style={signOutBtnStyle}>{t('sp_sign_out', 'Sign out')}</button>
        </div>
      </header>

      <div style={{ maxWidth: 1100, margin: '32px auto', padding: '0 24px' }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 28, color: 'var(--secondary-950)' }}>
          {t('sp_welcome_user', 'Welcome,')} {user?.first_name}!
        </h1>
        <p style={{ margin: '0 0 24px', color: 'var(--text-muted)', fontSize: 15 }}>
          {course?.CourseName ? `${course.CourseName} · ` : ''}{t('sp_your_stay_play_portal', 'Your Stay & Play Portal')}
        </p>

        {loading && (
          <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
            {t('loading_ellipsis', 'Loading...')}
          </div>
        )}

        {/* Setup checklist — only shown while there's still something to do.
            Once all 4 steps are complete, the checklist disappears and the
            NavCard grid below is the only navigation surface. Merchants can
            still revisit any step from those cards. */}
        {!loading && !isComplete && (
          <div className="card" style={{ padding: 28, marginBottom: 24, border: '2px solid var(--brand-200)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: 1.5, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 4 }}>
                  {t('sp_get_ready_to_launch', 'GET READY TO LAUNCH')}
                </div>
                <h2 style={{ margin: 0, fontSize: 22, color: 'var(--secondary-950)' }}>
                  {t('sp_finish_setting_up_portal', 'Finish setting up your portal')}
                </h2>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--brand-600)' }}>{completedCount}/{steps.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('sp_steps_complete', 'steps complete')}</div>
              </div>
            </div>

            <div style={{ height: 8, background: 'var(--card-border)', borderRadius: 4, overflow: 'hidden', margin: '16px 0 24px' }}>
              <div style={{
                height: '100%',
                width: progressPct + '%',
                background: 'linear-gradient(90deg, var(--brand-400), var(--brand-600))',
                transition: 'width 0.3s ease',
              }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {steps.map((step, i) => (
                <SetupStep key={step.key} step={step} num={i + 1} onClick={() => navigate(step.route)} />
              ))}
            </div>
          </div>
        )}

        {/* Once complete — show real dashboard content */}
        {!loading && isComplete && (
          <div style={{ marginBottom: 24 }}>
            <div className="card" style={{
              padding: 20,
              borderLeft: '4px solid var(--brand-500)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px', color: 'var(--secondary-950)' }}>{t('sp_youre_live', 'You\'re live!')}</h3>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>
                  {t('sp_widget_ready_to_embed', 'Your Stay & Play widget is ready to embed on your website.')}
                </p>
              </div>
              <button className="btn btn-primary" onClick={() => navigate('/portal/embed')} style={{ padding: '10px 20px' }}>
                {t('sp_get_embed_code', 'Get Embed Code')}
              </button>
            </div>
          </div>
        )}

        {/* Quick navigation cards — clickable links to portal sections */}
        {!loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            <NavCard
              title={t('sp_nav_packages', 'Packages')}
              desc={t('sp_nav_packages_desc', 'Create and edit your Stay & Play packages.')}
              icon="⛳"
              live={true}
              onClick={() => navigate('/portal/packages')}
            />
            <NavCard
              title={t('sp_nav_branding', 'Branding')}
              desc={t('sp_nav_branding_desc', 'Logo, colors, fonts, and widget style.')}
              icon="🎨"
              live={true}
              onClick={() => navigate('/portal/branding')}
            />
            <NavCard
              title={t('sp_nav_tee_sheet', 'Tee Sheet')}
              desc={t('sp_nav_tee_sheet_desc', 'Connect or change your tee sheet provider.')}
              icon="📅"
              live={true}
              onClick={() => navigate('/portal/tee-sheet')}
            />
            <NavCard
              title={t('sp_nav_course_location', 'Course Location')}
              desc={t('sp_nav_course_location_desc', 'Update your course address and coordinates.')}
              icon="📍"
              live={true}
              onClick={() => navigate('/portal/location')}
            />
            <NavCard
              title={t('sp_nav_bookings', 'Bookings')}
              desc={t('sp_nav_bookings_desc', 'Recent reservations and customer details.')}
              icon="📋"
              live={true}
              onClick={() => navigate('/portal/bookings')}
            />
            <NavCard
              title={t('embed_code', 'Embed Code')}
              desc={t('sp_nav_embed_code_desc', 'Get the code to paste on your website.')}
              icon="</>"
              live={true}
              onClick={() => navigate('/portal/embed')}
            />
            <NavCard
              title={t('sp_nav_analytics', 'Analytics')}
              desc={t('sp_nav_analytics_desc', 'See bookings, GMV, conversion funnel, and more.')}
              icon="📈"
              live={true}
              onClick={() => navigate('/portal/analytics')}
            />
            <NavCard
              title={t('sp_nav_reviews', 'Reviews')}
              desc={t('sp_nav_reviews_desc', 'Approve customer reviews to publish them on your widget.')}
              icon="⭐"
              live={true}
              onClick={() => navigate('/portal/reviews')}
            />
          </div>
        )}

        {/* ── Other InteGolf products ───────────────────────────────────── */}
        {!loading && (
          <div style={{ marginTop: 40 }}>
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, color: 'var(--secondary-950)' }}>
                {t('sp_explore_integolf_products', 'Explore InteGolf Products')}
              </h2>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>
                {t('sp_explore_integolf_products_desc', 'Use the same login across every InteGolf product. Active ones open directly; others link to learn more.')}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
              {PRODUCT_CATALOG.map(meta => {
                const status = productCatalog.find(p => p.productKey === meta.key) || null
                return <ProductTile key={meta.key} meta={meta} status={status} />
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Product catalog metadata ───────────────────────────────────────────
// Hardcoded because the product list doesn't change often. Enrollment status
// is fetched from /api/play-stay-portal/products and merged with this at render.
//
// URL base defaults to the dev site for now. Override with VITE_TOURNAMENT_WEB_URL
// in .env.production once the live tournament site is up.

const TOURNAMENT_WEB = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TOURNAMENT_WEB_URL)
  || 'https://tournament-dev2.integolf.com'

const PRODUCT_CATALOG = [
  {
    key: 'play_stay',
    name: 'Stay and Play',
    icon: '🏌️',
    desc: 'Golf + hotel packages sold directly on your site.',
    tagline: 'Bundle tee times and hotel nights into packaged trips customers book in one checkout.',
    features: [
      'Single-course AND multi-course package builder',
      'Automatic hotel availability from Expedia with price-cap filtering',
      'Embed widget that drops onto your existing website',
      'Automated payouts to partner courses with commission splits',
    ],
    launchUrl: '/portal',
    learnUrl: 'https://integolf.com/stay-and-play',
    isThisApp: true,
  },
  {
    key: 'tee_sheet',
    name: 'Tee Sheet',
    icon: '📅',
    desc: 'Online tee-time booking, pricing rules, cart fleet, and point-of-sale.',
    tagline: 'The complete online booking system for your course.',
    features: [
      'Real-time tee-time availability with dynamic pricing rules',
      'Cart fleet management + GPS tracking',
      'Integrated point-of-sale for pro shop and food & beverage',
      'Waitlist, online cancellations, and automated SMS reminders',
    ],
    launchUrl: `${TOURNAMENT_WEB}/tee-sheet`,
    learnUrl: 'https://integolf.com/tee-sheet',
  },
  {
    key: 'tournaments',
    name: 'Tournament Software',
    icon: '🏆',
    desc: 'Flights, pairings, scoring, leaderboards, and payouts.',
    tagline: 'Run every tournament format your course hosts, from Saturday scrambles to Ryder Cup weekends.',
    features: [
      'Stroke play, match play, scramble, best ball, Ryder Cup formats',
      'Drag-and-drop flight builder with automated pairings',
      'Live leaderboards golfers see on their phones',
      'Integrated payouts via Stripe or Moneris',
    ],
    launchUrl: `${TOURNAMENT_WEB}/tournaments`,
    learnUrl: 'https://integolf.com/tournaments',
  },
  {
    key: 'leagues',
    name: 'League Software',
    icon: '📊',
    desc: 'Weekly leagues, handicaps, standings, and mobile scoring.',
    tagline: 'Weekly leagues with handicaps, ringer boards, and mobile score entry.',
    features: [
      'Six scoring engines: Stableford, Nassau, BBB, 9-Point, 22-Point, Quota',
      'Auto-handicap calculation and season-long ringer boards',
      'Subs, manual presses, and split-season playoffs',
      'Mobile scoring via QR code — no app install required',
    ],
    launchUrl: `${TOURNAMENT_WEB}/leagues`,
    learnUrl: 'https://integolf.com/leagues',
  },
  {
    key: 'website_builder',
    name: 'Website Builder',
    icon: '🎨',
    desc: 'Build your course website with embedded widgets.',
    tagline: 'Drag-and-drop your course website with live InteGolf widgets built in.',
    features: [
      '13 widget types: tournaments, leaderboards, rates, photo galleries, Stay & Play, and more',
      'Custom domain support with automatic SSL via Cloudflare',
      'Embedded tee-time booking and Stay & Play widgets — no iframe tax',
      'SEO-ready course pages with photo galleries and contact forms',
    ],
    launchUrl: `${TOURNAMENT_WEB}/website-builder/course`,
    learnUrl: 'https://integolf.com/website-builder',
  },
]

// ─── Product tile (with inline-expanding Learn More panel) ──────────────

function ProductTile({ meta, status }) {
  const { t } = useLanguage()
  const [expanded, setExpanded] = useState(false)
  const enrolled = !!status?.enrolled
  const setupComplete = !!status?.setupComplete
  const isCurrent = meta.isThisApp

  // Status chip (top-right of the card)
  let chip, chipBg, chipFg
  if (isCurrent) {
    chip = t('sp_chip_you_are_here', 'YOU ARE HERE'); chipBg = '#dcfce7'; chipFg = '#166534'
  } else if (enrolled && setupComplete) {
    chip = t('sp_chip_active', 'ACTIVE'); chipBg = '#dcfce7'; chipFg = '#166534'
  } else if (enrolled) {
    chip = t('sp_chip_setup_in_progress', 'SETUP IN PROGRESS'); chipBg = '#fef3c7'; chipFg = '#92400e'
  } else {
    chip = t('sp_chip_not_enrolled', 'NOT ENROLLED'); chipBg = '#f1f5f9'; chipFg = '#475569'
  }

  const launchExternal = meta.launchUrl && /^https?:/.test(meta.launchUrl)

  return (
    <div className="card" style={{
      padding: 18,
      position: 'relative',
      opacity: isCurrent ? 0.85 : 1,
      minHeight: 180,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.15s',
    }}>
      <span style={{
        position: 'absolute', top: 10, right: 10,
        fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
        padding: '2px 8px', borderRadius: 10,
        background: chipBg, color: chipFg,
      }}>{chip}</span>
      <div style={{ fontSize: 26, marginBottom: 6 }} aria-hidden="true">{meta.icon}</div>
      <h3 style={{ margin: '0 0 6px', fontSize: 15, color: 'var(--secondary-950)' }}>{meta.name}</h3>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45, flex: 1 }}>{meta.desc}</p>

      {/* Expanded details panel */}
      {expanded && (
        <div style={{
          background: 'var(--brand-50, #f0fdfa)',
          border: '1px solid var(--brand-100, #ccfbf1)',
          borderRadius: 8,
          padding: 14,
          marginBottom: 12,
          fontSize: 12,
          lineHeight: 1.55,
          color: 'var(--secondary-950)',
        }}>
          {meta.tagline && (
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--brand-700, #0f766e)' }}>
              {meta.tagline}
            </div>
          )}
          {Array.isArray(meta.features) && meta.features.length > 0 && (
            <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
              {meta.features.map((f, i) => (
                <li key={i} style={{ marginBottom: 4 }}>{f}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Action row */}
      {isCurrent ? (
        // Current app: just a toggle for the features list (no launch button — they're here)
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="btn btn-outline-dark"
            style={{ padding: '7px 12px', fontSize: 12 }}
          >
            {expanded ? t('sp_hide_details_arrow', 'Hide details ▴') : t('sp_whats_included_arrow', 'What\'s included ▾')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="btn btn-outline-dark"
            style={{ padding: '7px 12px', fontSize: 12 }}
          >
            {expanded ? t('sp_hide_arrow', 'Hide ▴') : t('sp_learn_more_arrow', 'Learn more ▾')}
          </button>
          <a
            href={meta.launchUrl}
            target={launchExternal ? '_blank' : undefined}
            rel={launchExternal ? 'noopener noreferrer' : undefined}
            className="btn btn-primary"
            style={{ padding: '7px 12px', fontSize: 12, textDecoration: 'none', display: 'inline-block' }}
          >
            {t('sp_launch_product_arrow', 'Launch Product →')}
          </a>
        </div>
      )}
    </div>
  )
}

// ─── Setup step row ──────────────────────────────────────────────────────

function SetupStep({ step, num, onClick }) {
  const { t } = useLanguage()
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '14px 16px',
        background: step.complete ? '#f0fdf4' : '#fff',
        border: '1px solid ' + (step.complete ? '#bbf7d0' : 'var(--card-border)'),
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
    >
      <div
        aria-label={step.complete ? t('done', 'Done') : t('step', 'Step') + ' ' + num}
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
          fontWeight: 700,
          background: step.complete ? 'var(--brand-500)' : 'var(--secondary-100)',
          color: step.complete ? '#fff' : 'var(--text-muted)',
          flexShrink: 0,
        }}
      >
        {step.complete ? '✓' : num}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: 600,
          fontSize: 15,
          color: 'var(--secondary-950)',
        }}>
          {step.title}
          {step.complete && (
            <span style={{
              marginLeft: 8,
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 10,
              background: '#dcfce7',
              color: '#166534',
              letterSpacing: 0.5,
            }}>
              {t('sp_done_caps', 'DONE')}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
          {step.desc}
        </div>
      </div>
      <button
        className={step.complete ? 'btn btn-outline-dark' : 'btn btn-primary'}
        style={{ padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap' }}
        onClick={(e) => { e.stopPropagation(); onClick() }}
      >
        {step.complete ? t('edit', 'Edit') : t('sp_start_arrow', 'Start →')}
      </button>
    </div>
  )
}

// ─── Navigation card ─────────────────────────────────────────────────────

function NavCard({ title, desc, icon, live, onClick }) {
  const { t } = useLanguage()
  return (
    <div
      onClick={live ? onClick : undefined}
      className="card"
      style={{
        padding: 20,
        cursor: live ? 'pointer' : 'default',
        opacity: live ? 1 : 0.55,
        transition: 'all 0.15s',
        position: 'relative',
        ...(live ? { borderColor: 'var(--card-border)' } : {}),
      }}
      onMouseEnter={e => { if (live) e.currentTarget.style.borderColor = 'var(--brand-400)' }}
      onMouseLeave={e => { if (live) e.currentTarget.style.borderColor = 'var(--card-border)' }}
    >
      {!live && (
        <span style={{
          position: 'absolute', top: 12, right: 12,
          fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
          background: '#fef3c7', color: '#92400e', letterSpacing: 0.5,
        }}>
          {t('sp_coming_soon_caps', 'COMING SOON')}
        </span>
      )}
      <div style={{ fontSize: 24, marginBottom: 8 }} aria-hidden="true">{icon}</div>
      <h3 style={{ margin: '0 0 6px', fontSize: 16, color: 'var(--secondary-950)' }}>{title}</h3>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>{desc}</p>
    </div>
  )
}

const headerStyle = {
  background: '#fff',
  borderBottom: '1px solid var(--card-border)',
  padding: '16px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}

const signOutBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
}
